<template>
  <div class="ts-cardlist px-3 pb-4">
    <div v-if="error && !loading" class="py-10 text-center text-gray-600">
      <p class="mb-2">Something went wrong loading this list.</p>
      <p class="mb-3 text-xs text-gray-400">{{ error }}</p>
      <button
        class="px-4 py-2 text-sm text-white bg-blue-700 rounded-lg"
        data-cy="server-table-retry"
        @click="emit('retry')"
      >
        Retry
      </button>
    </div>
    <div
      v-else-if="!rows.length && !loading"
      class="py-12 text-center text-gray-500"
    >
      No matching records found
    </div>
    <div
      v-for="(row, i) in rows"
      :key="rowKey(row, i)"
      class="ts-card"
      :data-row-id="row.DT_RowId"
    >
      <div class="flex-1 min-w-0">
        <div class="ts-card-primary" v-html="cell(row, config.primary)" />
        <div
          v-for="key in config.secondary"
          :key="key"
          class="ts-card-secondary"
          v-html="cell(row, key)"
        />
        <div class="ts-card-meta">
          <span
            v-if="config.status && cell(row, config.status)"
            class="ts-card-status"
            v-html="cell(row, config.status)"
          />
          <template v-for="(key, i) in config.meta" :key="key">
            <span v-if="i > 0" aria-hidden="true"> · </span>
            <span v-html="cell(row, key)" />
          </template>
        </div>
      </div>
      <div
        v-if="config.actions"
        class="ts-card-menu"
        :data-col="config.actions"
        v-html="cell(row, config.actions)"
      />
    </div>
    <button
      v-if="canLoadMore"
      class="ts-card-more"
      :disabled="loading"
      data-cy="card-load-more"
      @click="emit('load-more')"
    >
      {{ loading ? "Loading…" : `Load ${pageSize} more` }}
    </button>
    <div class="ts-card-count">Showing {{ rows.length }} of {{ total }}</div>
  </div>
</template>

<script setup lang="ts">
import type { TableRow } from "./useServerTable";

// Card layout per the TB-2201 sketch: primary line (name-like column),
// secondary lines, then a metadata line with an optional status pill. The
// values are the same server-rendered HTML strings the table's <td>s show,
// read off the SAME rows the table renders from.
export interface CardConfig {
  primary: string;
  secondary: string[];
  meta: string[];
  status?: string;
  actions?: string;
}

defineProps<{
  rows: TableRow[];
  config: CardConfig;
  loading: boolean;
  error?: string | null;
  total: number;
  pageSize: number;
  canLoadMore: boolean;
}>();

const emit = defineEmits<{ (e: "load-more"): void; (e: "retry"): void }>();

const rowKey = (row: TableRow, i: number) =>
  row.DT_RowId != null ? String(row.DT_RowId) : String(i);
const cell = (row: TableRow, key: string) => {
  const v = row[key];
  return v == null ? "" : String(v);
};
</script>

<style scoped>
.ts-card {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 8px;
}
.ts-card-primary {
  font-size: 16px;
  font-weight: 650;
  color: #2563eb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ts-card-secondary {
  font-size: 13px;
  color: #6b7280;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ts-card-meta {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
}
.ts-card-status {
  margin-right: 4px;
}
.ts-card-menu {
  flex: none;
}
.ts-card-more {
  display: block;
  width: calc(100% - 8px);
  margin: 4px auto 0;
  padding: 11px;
  text-align: center;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #2563eb;
  font-weight: 600;
  font-size: 14px;
}
.ts-card-more:disabled {
  opacity: 0.5;
}
.ts-card-count {
  text-align: center;
  font-size: 12px;
  color: #6b7280;
  padding: 8px 0 0;
}
</style>
